import {StorageService} from '../../services/storage/storage.service';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {take} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalRescheduleAppointmentComponent} from '../modal-reschedule-appointment/modal-reschedule-appointment.component';
import {ClinicState, IClinic} from '../../models/clinic';
import {firstValueFrom, Observable} from 'rxjs';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {AppointmentActions, AppointmentState, AppointmentStatusEnum, IAppointment} from '../../models/appointment';
import {Loader} from '../../models/loader/loader.redux';
import {Logger} from '../../class/helper/logger.class';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Store} from '@ngxs/store';
import {cloneDeep} from 'lodash';
import {AppointmentServiceService} from 'src/app/services/appointment-service/appointment-service.service';
import {ServiceAction} from '../../models/service';

@Component({
  selector: 'app-appointments-table',
  templateUrl: './appointments-table.component.html',
  styleUrls: ['./appointments-table.component.scss']
})
export class AppointmentsTableComponent implements OnInit, OnChanges {
  @Input() appointments: IAppointment[] = [];
  @Input() maxVisibleRows = 5;
  @Input() hidden = false;
  @Input() userType: 'patient' | 'expert';
  @Input() showMedicalObjectives: boolean;
  @Output() mainAction = new EventEmitter<IAppointment>();
  @Output() addNote = new EventEmitter<string>();
  @Output() noteAction = new EventEmitter<{ serviceId, appointmentId: string, usersAppointment: any[] }>();

  // variables para modal de ver detalles de cita
  clinic$: Observable<IClinic> = this.store.select<IClinic>(ClinicState.getClinicSelected);
  @ViewChild(ModalRescheduleAppointmentComponent) modalRescheduleAppointmentComponent: ModalRescheduleAppointmentComponent;
  appointmentIndex: number;
  isVisibleModal: boolean;
  isVisibleCancelAppointmentModal: boolean;
  isVisibleRescheduleAppointmentModal: boolean;
  isAppointmentSelectedCompleted: boolean;

  confirmModal?: NzModalRef;
  appointmentURL: IAppointment;

  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService,
    private router: Router,
    private modal: NzModalService,
    private activatedRoute: ActivatedRoute,
    private appointmentService: AppointmentServiceService,
    private storageService: StorageService
  ) { }

  async ngOnInit(): Promise<void> {
    const appointmentId = this.activatedRoute.snapshot.queryParams.appointmentId;
    if (appointmentId) {
      await firstValueFrom(this.store.dispatch(new AppointmentActions.GetAppointmentURL(appointmentId)));
      this.appointmentURL = await firstValueFrom(this.store.select(AppointmentState.getAppointmentURL));
    }
  }

  /**
   * Detecta cuando cambia la propiedad appointments para agregar el número de notas de cada cita.
   * @param changes cambios
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.appointments) return;
    this.appointments = cloneDeep([...changes.appointments.currentValue]);
    this.appointments.forEach(appointment => {
      appointment.totalNotes = this.getNotesCount(appointment.users);
    });
  }

  /**
   * obtiene el total de notas que tiene un usuario en la cita
   * @param users usuarios
   */
  getNotesCount(users): number {
    let total = 0;
    users.forEach(user => {
      if (user.hasOwnProperty('noteCount')) {
        total += user.noteCount ? user.noteCount : 0;
      }
    });
    return total;
  }

  /**
   * Emite datos al componente padre cuando se quieren ver las notas de una cita.
   * @param serviceId id del servicio
   * @param appointmentId id de la cita
   * @param usersAppointment usuarios
   */
  onEmitViewNotes(serviceId: string, appointmentId: string, usersAppointment: any[]): void {
    this.noteAction.emit({ serviceId, appointmentId, usersAppointment });
  }

  /**
   * Emite al componente padre cuando se quiere agregar una nueva nota a una cita.
   * @param appointmentId id de la cita
   */
  onEmitAddNote(appointmentId: string): void {
    this.addNote.emit(appointmentId);
  }


  /**
   * Muestra en un modal los detalles de la cita seleccionada
   * @param index índice de la cita
   */
  async onViewDetails(index: number): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true, 'Cargando detalles de la cita ...'))));
    const appointment = this.appointments[index];
    try {
      this.isAppointmentSelectedCompleted = await this.appointmentService.isAppointmentCompleted(appointment._id);
      const appointmentWithExpert = cloneDeep(await firstValueFrom((this.store.selectOnce(AppointmentState.getAppointmentSelected))));
      if (appointmentWithExpert) this.appointments[index].expert = appointmentWithExpert.expert;
      if (appointmentWithExpert) this.appointments[index].clinicBranch = appointmentWithExpert.clinicBranch;
      if (appointmentWithExpert) this.appointments[index].metadata = appointmentWithExpert.metadata;
    } catch (e) {
      Logger.log(e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
    this.appointmentIndex = index;
    this.isVisibleModal = true;
  }

  /**
   * Función que valida la acción en el modal de cancelar cita.
   * "action" = false para cerrar modal; true para cancelar cita
   */
  async onCloseModalCancelAppointment(action: boolean): Promise<void> {
    if (action) {
      await firstValueFrom((this.store.dispatch(new Loader(true))));
      try {
        await firstValueFrom(this.store.dispatch(
          new AppointmentActions.Cancel(this.appointments[this.appointmentIndex]?._id)
        ));
        this.appointments[this.appointmentIndex].status = AppointmentStatusEnum.CANCELLED;
        this.isVisibleCancelAppointmentModal = false;
        this.isVisibleModal = false;

        this.messageAlertService.createMessage('success', '¡Cita cancelada con éxito!');
      } catch (e) {
        Logger.log(e?.message);
        this.messageAlertService.createMessage('error', this.messageAlertService.getMessageError(e?.message));
      } finally {
        await firstValueFrom((this.store.dispatch(new Loader(false))));
      }
    }
    this.isVisibleCancelAppointmentModal = false;
  }

  /**
   * Función que valida la acción en el modal de reprogramar cita.
   * "action" = false para cerrar modal; true para reprogramar cita
   */
  async onCloseModalRescheduleAppointment(action: boolean): Promise<void> {
    if (action) {
      await firstValueFrom((this.store.dispatch(new Loader(true))));
      try {
        const appointment = this.modalRescheduleAppointmentComponent.getValue();
        await firstValueFrom(this.store.dispatch(
          new AppointmentActions.Reschedule(this.appointments[this.appointmentIndex]?._id, appointment)
        ));
        const appointmentRedux: IAppointment = cloneDeep(await firstValueFrom((this.store.selectOnce(AppointmentState.getAppointmentSelected))));
        this.messageAlertService.createMessage('success', '¡Cita reprogramada con éxito!');
        this.appointments[this.appointmentIndex].status = AppointmentStatusEnum.TO_RESCHEDULE;
        this.appointments[this.appointmentIndex].startDate = appointmentRedux.startDate;
        this.appointments[this.appointmentIndex].endDate = appointmentRedux.endDate;
        this.isVisibleRescheduleAppointmentModal = false;
        this.isVisibleModal = false;
        this.modalRescheduleAppointmentComponent.resetModal();
      } catch (e) {
        Logger.log('Ocurrio un error', e?.message);
        this.messageAlertService.createMessage('error', this.messageAlertService.getMessageError(e?.message));
      } finally {
        await firstValueFrom((this.store.dispatch(new Loader(false))));
      }
    } else {
      this.modalRescheduleAppointmentComponent.resetModal();
      this.isVisibleRescheduleAppointmentModal = false;
    }
  }

  /**
   * Redirige a la vista de consultas para los pacientes de la cita, para el paciente seleccionado
   */
  async onGoToConsultation(): Promise<void> {
    // Verificar si hay cita en la URL
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    const clinic = await firstValueFrom(this.clinic$.pipe(take(1)));
    const appointmentId = this.activatedRoute.snapshot.queryParams.appointmentId;
    const selectedTableAppointment = await firstValueFrom(this.store.selectOnce(AppointmentState.getAppointmentSelected));

    // Si no hay cita en la URL entonces simplemente redirigir a la vista
    if (!appointmentId) {
      await this.navigateToConsultation(clinic, selectedTableAppointment);
      await firstValueFrom((this.store.dispatch(new Loader(false))));
      return;
    }

    // Si hay cita en la URL, traer la cita y comprobar su status
    if (!this.appointmentURL) {
      await firstValueFrom(this.store.dispatch(new AppointmentActions.GetAppointmentURL(appointmentId)));
      this.appointmentURL = await firstValueFrom(this.store.select(AppointmentState.getAppointmentURL));
    }
    const isCompletedAppointmentURL = this.appointmentURL.status === AppointmentStatusEnum.COMPLETED ||
      this.appointmentURL.status === AppointmentStatusEnum.CANCELLED;

    // Si su estatus (cita URL) es completado entonces simplemente redirigir a la vista
    if (isCompletedAppointmentURL) {
      await this.navigateToConsultation(clinic, selectedTableAppointment);
      await firstValueFrom((this.store.dispatch(new Loader(false))));
      return;
    }

    // Si su estatus (cita URL) no está completado, y la cita que se quiere acceder ya terminó, redirigir a la vista
    if (this.isAppointmentSelectedCompleted) {
      await this.navigateToConsultation(clinic, selectedTableAppointment);
      await firstValueFrom((this.store.dispatch(new Loader(false))));
      return;
    }

    // Si su estatus (cita URL) no está completado, y la cita que se quiere acceder no ha iniciado, mostrar modal
    await firstValueFrom((this.store.dispatch(new Loader(false))));
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Deseas iniciar una nueva cita?',
      nzContent: 'La cita actual será completada para iniciar la siguiente.',
      nzOkText: 'SÍ INICIAR',
      nzCancelText: 'NO INICIAR',
      nzClassName: 'modal-confirm button-primary',
      nzWidth: '500px',
      nzClosable: false,
      nzOnOk: async () => {
        const duration: number = this.storageService.getLocalItem('durationAppointment') || 0;
        await firstValueFrom((this.store.dispatch(new ServiceAction.CompleteAppointment(appointmentId, duration))));
        await this.navigateToConsultation(clinic, selectedTableAppointment);
      },
      nzOnCancel: () => {
        return;
      },
    });
  }

  async navigateToConsultation(clinic: IClinic, appointmentSelected: IAppointment): Promise<void> {
    await this.router.navigate([
      'clinic',
      clinic._id,
      'patients',
      appointmentSelected.users[0].patientId,
      'consultation',
      'information'
    ],
    {
      queryParams: {
        appointmentId: appointmentSelected._id
      }
    });
  }

}
